'use client'

import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
  rankedNavItems,
  MOBILE_VISIBLE_COUNT,
  maisNavItem
} from '@/features/navigation'
import { useNavOverflow } from '@/shared/contexts/NavOverflow'
import { useBreakpoints } from '@/shared/hooks/useBreakpoints'
import { AppLink } from '@/shared/lib/router'
import { cn } from '@/shared/lib/utils'

import { ShellMainNavItem } from './NavItem'

interface ShellMainNavProps {
  className?: string
}

// NavItem.tsx renders each row at a fixed `h-[52px]` with `gap-2` (8px)
// between siblings — keep these in sync if that sizing ever changes.
const ITEM_ROW_HEIGHT = 52
const ITEM_GAP = 8

// Matches Shell/index.tsx's `pb-6` on the rail — the bit of reserved bottom
// space no ref can see since nothing renders inside it.
const ROOT_BOTTOM_PADDING = 24

export const ShellMainNav = ({ className = '' }: ShellMainNavProps) => {
  const { isAtLeast } = useBreakpoints()
  const isDesktop = isAtLeast('lg')
  const isExpanded = isAtLeast('xl')
  const { setOverflowItems } = useNavOverflow()

  const rootRef = useRef<HTMLDivElement>(null)
  const logoRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const [fitCount, setFitCount] = useState(rankedNavItems.length)

  useLayoutEffect(() => {
    if (!isDesktop) return

    const computeFit = () => {
      const root = rootRef.current
      const logo = logoRef.current
      const footer = footerRef.current
      if (!root || !logo || !footer) return

      const availableHeight =
        root.getBoundingClientRect().bottom -
        ROOT_BOTTOM_PADDING -
        logo.getBoundingClientRect().bottom -
        footer.getBoundingClientRect().height

      // Total rows that fit, then reserve one for the always-rendered MAIS
      // row (it lives in the same list, not budgeted separately).
      const totalSlots = Math.floor(
        (availableHeight + ITEM_GAP) / (ITEM_ROW_HEIGHT + ITEM_GAP)
      )
      const count = Math.max(0, Math.min(rankedNavItems.length, totalSlots - 1))

      setFitCount(count)
      setOverflowItems(rankedNavItems.slice(count))
    }

    computeFit()

    const observer = new ResizeObserver(computeFit)
    observer.observe(rootRef.current!)
    observer.observe(footerRef.current!)

    return () => observer.disconnect()
  }, [isDesktop, setOverflowItems])

  const navItems = useMemo(() => {
    if (isDesktop) return rankedNavItems.slice(0, fitCount)
    return rankedNavItems.slice(0, MOBILE_VISIBLE_COUNT)
  }, [isDesktop, fitCount])

  return (
    <div ref={rootRef} className={cn('z-40 flex', className)}>
      {isDesktop && (
        <div ref={logoRef} className="shrink-0 pb-10">
          <AppLink
            href="/jornadas"
            aria-label="Missão Futuro"
            className="block ps-4"
          >
            <img
              src={
                isExpanded
                  ? '/brand/logo-horizontal-white.svg'
                  : '/brand/logo-icon-white.svg'
              }
              alt="Missão Futuro"
              className={isExpanded ? 'h-11 w-auto' : 'size-9'}
            />
          </AppLink>
        </div>
      )}

      <div className={cn('flex gap-2 lg:flex-col', isDesktop && 'flex-1')}>
        {navItems.map(item => (
          <ShellMainNavItem
            key={item.href}
            href={item.href}
            ariaLabel={item.title}
          >
            <img
              src={`/icons/${item.iconSlug}.svg`}
              className="size-9 lg:size-8"
              alt=""
              aria-hidden
            />
            {isAtLeast('xl') && (
              <span className="block text-sm uppercase">{item.title}</span>
            )}
          </ShellMainNavItem>
        ))}
        <ShellMainNavItem href={maisNavItem.href} ariaLabel={maisNavItem.title}>
          <img
            src={`/icons/${maisNavItem.iconSlug}.svg`}
            className="size-9 lg:size-8"
            alt=""
            aria-hidden
          />
          {isAtLeast('xl') && (
            <span className="block text-sm uppercase">{maisNavItem.title}</span>
          )}
        </ShellMainNavItem>
      </div>

      {isDesktop && (
        <div ref={footerRef} className="shrink-0 pt-6 pb-4">
          <div className="border-t border-white/10 pt-6 ps-4 pe-2">
            <div
              className={cn(
                'mb-2 text-[10px] font-bold uppercase tracking-widest text-white/40',
                !isExpanded && 'sr-only'
              )}
            >
              Realização
            </div>
            <img
              src="/brand/sebrae-educacao-empreendedora-white.png"
              alt="Sebrae — Educação Empreendedora"
              className="h-auto max-w-full"
            />
          </div>
        </div>
      )}
    </div>
  )
}
